import { useState, type FormEvent } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  MIN_PASSWORD_LENGTH,
  signInWithPassword,
  signUpWithInvite,
  startSignIn,
  type OAuthProvider,
} from '@/lib/auth';

/**
 * Sign-in / Sign-up route.
 *
 * 3 つのフローが同居する:
 *   1. メール + パスワードの **サインイン** (`signInWithPassword`) — 同期完了 → `/`。
 *   2. **招待コード付き新規登録** (`signUpWithInvite`) — apps/api がサーバ側で
 *      招待コードを検証してユーザーを作成 (Supabase signup は無効のまま =
 *      完全招待制)。成功したらそのままログイン → `/`。spec #44。
 *   3. OAuth (`startSignIn`) — PKCE を開始しプロバイダへリダイレクト。
 *
 * サインインと新規登録は 1 画面でモード切替 (トグルリンク)。
 */
type Mode = 'signin' | 'signup';

/** サーバのエラーコード → ユーザー向け文言。詳細は露呈しない。 */
function signupErrorMessage(code: string): string {
  switch (code) {
    case 'invalid_invite':
      return '招待コードが無効か、すでに使用されています。';
    case 'rate_limited':
      return '試行回数が多すぎます。しばらくしてからお試しください。';
    case 'registration_failed':
      return '登録できませんでした。入力内容を確認してください。';
    case 'network_error':
      return '通信エラーが発生しました。接続を確認して再度お試しください。';
    default:
      return '登録できませんでした。しばらくしてからお試しください。';
  }
}

function SignInPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSignup = mode === 'signup';

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (isSignup) {
      if (password.length < MIN_PASSWORD_LENGTH) {
        setError(`パスワードは ${MIN_PASSWORD_LENGTH} 文字以上にしてください。`);
        return;
      }
      if (password !== confirmPassword) {
        setError('パスワードが一致しません。');
        return;
      }
    }
    setSubmitting(true);
    try {
      if (isSignup) {
        await signUpWithInvite(email, password, inviteCode.trim());
      } else {
        await signInWithPassword(email, password);
      }
      await navigate({ to: '/' });
    } catch (err) {
      const code = err instanceof Error ? err.message : '';
      setError(isSignup ? signupErrorMessage(code) : code || 'sign-in failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleOAuth(provider: OAuthProvider) {
    setError(null);
    try {
      const url = await startSignIn(provider);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'oauth failed');
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{isSignup ? '新規登録' : 'サインイン'}</CardTitle>
          <CardDescription>
            {isSignup
              ? '招待コードとメール・パスワードで Quon のアカウントを作成します。'
              : 'Quon にサインインして AI コンパニオンを呼び出します。'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">メール</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">パスワード</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={isSignup ? MIN_PASSWORD_LENGTH : undefined}
                autoComplete={isSignup ? 'new-password' : 'current-password'}
              />
              {isSignup ? (
                <p className="text-xs text-muted-foreground">{MIN_PASSWORD_LENGTH} 文字以上</p>
              ) : (
                <Link
                  to="/auth/forgot-password"
                  className="self-end text-xs text-muted-foreground underline underline-offset-2 hover:opacity-80"
                >
                  パスワードを忘れた場合
                </Link>
              )}
            </div>
            {isSignup ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="confirm-password">パスワード（確認）</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="invite-code">招待コード</Label>
                  <Input
                    id="invite-code"
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    required
                    autoComplete="off"
                  />
                </div>
              </>
            ) : null}
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <Button type="submit" disabled={submitting}>
              {submitting ? '送信中…' : isSignup ? 'アカウントを作成' : 'サインイン'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            {isSignup ? (
              <>
                すでにアカウントをお持ちですか？{' '}
                <button
                  type="button"
                  className="text-foreground underline underline-offset-2 hover:opacity-80"
                  onClick={() => switchMode('signin')}
                >
                  サインイン
                </button>
              </>
            ) : (
              <>
                招待コードをお持ちですか？{' '}
                <button
                  type="button"
                  className="text-foreground underline underline-offset-2 hover:opacity-80"
                  onClick={() => switchMode('signup')}
                >
                  新規登録
                </button>
              </>
            )}
          </p>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">または</span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button variant="outline" type="button" onClick={() => handleOAuth('google')}>
              Google で続ける
            </Button>
            <Button variant="outline" type="button" onClick={() => handleOAuth('github')}>
              GitHub で続ける
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/signin')({
  component: SignInPage,
});
